import React, { useState, useRef, useEffect } from "react";
import { Button } from "reactstrap";
import {
  FiPaperclip,
  FiFile,
  FiImage,
  FiVideo,
  FiMusic,
  FiTrash2,
} from "react-icons/fi";
import axios from "../../../axios";
import * as url from "../../../utils/url";

const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "mp4",
  "mp3",
  "m4a",
  "wav",
];
const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",");
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MMS_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
// Only words the hint; the BE decides MMS vs link at send time with the same
// limit (Twilio's 5 MB, minus a margin for the message text).
const MMS_IMAGE_MAX_BYTES = 4900000;
// A PDF this small goes as the file itself in a text; a larger one as a link.
const MMS_PDF_MAX_BYTES = 500000;

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const renderFileIcon = (contentType) => {
  if (contentType.indexOf("image/") === 0) {
    return <FiImage size={20} />;
  }
  if (contentType.indexOf("video/") === 0) {
    return <FiVideo size={20} />;
  }
  if (contentType.indexOf("audio/") === 0) {
    return <FiMusic size={20} />;
  }
  return <FiFile size={20} />;
};

const isImageFile = (attachment) => {
  return MMS_IMAGE_TYPES.indexOf(attachment.contentType) !== -1;
};

// An image small enough for a picture message has no link to warn about.
const isSentAsPicture = (attachment) => {
  return isImageFile(attachment) && attachment.sizeBytes < MMS_IMAGE_MAX_BYTES;
};

const isSentAsPdf = (attachment) => {
  return (
    attachment.contentType === "application/pdf" &&
    attachment.sizeBytes < MMS_PDF_MAX_BYTES
  );
};

const getDeliveryText = (attachment) => {
  if (isSentAsPicture(attachment)) {
    return "Texts: sent as a picture. Emails: shown in the email body.";
  }
  if (isImageFile(attachment)) {
    return "Texts: sent as a link (too large for a picture message). Emails: shown in the email body.";
  }
  if (isSentAsPdf(attachment)) {
    return "Texts: sent as a pdf. Emails: sent as a link.";
  }
  return "Texts and emails: sent as a link.";
};

// The size limit only matters while choosing a file; once one is attached
// just the confidentiality warning for it is shown.
const getFootnoteText = (attachment) => {
  if (!attachment) {
    return "Up to 5 MB.";
  }
  if (isSentAsPicture(attachment) || isSentAsPdf(attachment)) {
    return "Please don’t attach confidential images.";
  }
  return "Anyone with the link can open this file. Please don’t attach confidential files.";
};

const getServerMessage = (err) => {
  return (
    err.response &&
    err.response.data &&
    err.response.data.errors &&
    err.response.data.errors.message
  );
};

// One optional file for a Message. It uploads as soon as it is picked and gives
// the parent what the BE returned ({ attachment, attachmentUploadHandle }), or
// null on remove. The parent owns the attached file, so create and edit share it.
const MessageAttachmentPicker = ({
  attachment,
  onChange,
  onUploadingChange,
  disabled,
  disabledReason,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  // Local thumbnail of an image picked here, tied to the name it was made for.
  const [thumbnail, setThumbnail] = useState(null);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (thumbnail) {
        URL.revokeObjectURL(thumbnail.url);
      }
    };
  }, [thumbnail]);

  useEffect(() => {
    if (!attachment) {
      setThumbnail(null);
    }
  }, [attachment]);

  const updateUploading = (value) => {
    setIsUploading(value);
    if (onUploadingChange) {
      onUploadingChange(value);
    }
  };

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setError("");
    setProgress(0);
    updateUploading(true);

    axios
      .post(url.uploadMessageAttachment, formData, {
        onUploadProgress: (event) => {
          if (event.total && isMountedRef.current) {
            setProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      })
      .then(({ data }) => {
        if (!isMountedRef.current) {
          return;
        }
        if (file.type.indexOf("image/") === 0) {
          setThumbnail({
            url: URL.createObjectURL(file),
            name: data.data.attachment.name,
          });
        } else {
          setThumbnail(null);
        }
        onChange(data.data);
      })
      .catch((err) => {
        if (isMountedRef.current) {
          setError(getServerMessage(err) || "Could not upload the file. Please try again.");
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          updateUploading(false);
        }
      });
  };

  const onFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    // Cleared so choosing the same file again still triggers onChange.
    e.target.value = "";
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop().toLowerCase();
    if (ALLOWED_EXTENSIONS.indexOf(extension) === -1) {
      setError(
        "This file type isn't supported. Use JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX, MP4, MP3, M4A or WAV."
      );
      return;
    }
    if (file.size === 0) {
      setError("The file is empty.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("The file is too large. The maximum size is 5 MB.");
      return;
    }

    uploadFile(file);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isLocked = disabled || isUploading;

  const renderContent = () => {
    if (isUploading) {
      return <div className="small text-muted">Uploading... {progress}%</div>;
    }

    if (!attachment) {
      return (
        <Button size="sm" outline disabled={isLocked} onClick={openFilePicker}>
          <FiPaperclip size={14} className="mr-1" />
          Attach a file
        </Button>
      );
    }

    return (
      <div className="d-flex align-items-center border rounded p-2">
        {thumbnail && thumbnail.name === attachment.name ? (
          <img
            src={thumbnail.url}
            alt={attachment.name}
            width={40}
            height={40}
            className="mr-2 rounded"
            style={{ objectFit: "cover" }}
          />
        ) : (
          <span className="mr-2">{renderFileIcon(attachment.contentType)}</span>
        )}
        <div className="flex-grow-1 text-truncate">
          <div className="text-truncate">{attachment.name}</div>
          <div className="small text-muted">
            {formatFileSize(attachment.sizeBytes)} · {getDeliveryText(attachment)}
          </div>
        </div>
        <Button size="sm" color="link" disabled={isLocked} onClick={openFilePicker}>
          Replace
        </Button>
        <Button
          size="sm"
          color="link"
          className="text-danger"
          aria-label="Remove file"
          disabled={isLocked}
          onClick={() => onChange(null)}
        >
          <FiTrash2 size={16} />
        </Button>
      </div>
    );
  };

  return (
    <div className="my-3">
      <div className="small font-weight-bold mb-1">Attach a file (optional)</div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        className="d-none"
        onChange={onFileSelected}
      />

      {renderContent()}

      {disabled && disabledReason ? (
        <div className="small text-muted mt-1">{disabledReason}</div>
      ) : (
        ""
      )}
      {error ? <div className="small text-danger mt-1">{error}</div> : ""}
      <div className="small text-muted mt-1">{getFootnoteText(attachment)}</div>
    </div>
  );
};

export default MessageAttachmentPicker;
